import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'

import { creativeAiApi } from '../api/CreativeAiApi'
import type { CreativeAiSectionQuery } from '../api/CreativeAiApi'
import type {
  CombinationsQuery,
  CombinationsResponse,
  CombinationsScope,
} from '../models/combinations.types'
import { decodeCombinationsResponse } from '../service/combinations'

import { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

export interface CombinationsLoadInput {
  scope: CombinationsScope
  query: CombinationsQuery
}

const EMPTY_RESPONSE: CombinationsResponse = {
  working: [],
  not_working: [],
  meta: { hiddenBelowSampleSize: 0 },
  overallValue: null,
}

function requestKey({ scope, query }: CombinationsLoadInput): string {
  return [
    scope.accountId,
    scope.workspaceId,
    query.kind,
    query.metric,
    query.analysisId,
    query.platform ?? '',
    query.objective ?? '',
    query.campaignType ?? '',
  ]
    .map(String)
    .join('|')
}

function apiQuery(query: CombinationsQuery): CreativeAiSectionQuery {
  return {
    type: query.kind,
    metric: query.metric,
    analysis_id: query.analysisId,
    ...(query.platform ? { platform: query.platform } : {}),
    ...(query.objective ? { objective: query.objective } : {}),
    ...(query.campaignType ? { campaign_type: query.campaignType } : {}),
  }
}

function errorFrom(cause: unknown): ApiError {
  return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
}

function canceled(cause: unknown): boolean {
  return ApiError.isApiError(cause) && cause.kind === 'canceled'
}

/**
 * The sole owner of the `combinations` request. Legacy parity (`combNotFound`): a 404 means "no
 * combination run for this analysis yet" — an expected, renderable state, not a transport error.
 */
export const useCombinationsStore = defineStore('creativeAiCombinations', () => {
  const response = shallowRef<CombinationsResponse>(EMPTY_RESPONSE)
  const state = ref<UiAsyncState>('empty')
  const notFound = ref(false)
  const error = shallowRef<ApiError | null>(null)
  let activeKey: string | null = null
  let generation = 0
  let controller: AbortController | null = null
  let pending: Promise<void> | null = null

  async function load(input: CombinationsLoadInput): Promise<void> {
    const nextKey = requestKey(input)
    if (activeKey === nextKey && pending) return pending
    if (activeKey === nextKey && (state.value === 'ready' || state.value === 'empty')) return

    controller?.abort()
    const requestGeneration = ++generation
    const nextController = new AbortController()
    controller = nextController
    activeKey = nextKey
    state.value = 'loading'
    notFound.value = false
    error.value = null
    const request = (async () => {
      try {
        const raw = await creativeAiApi.getCombinations(
          input.scope,
          apiQuery(input.query),
          nextController.signal,
        )
        if (requestGeneration !== generation || nextController.signal.aborted) return
        response.value = decodeCombinationsResponse(raw)
        state.value =
          response.value.working.length || response.value.not_working.length ? 'ready' : 'empty'
      } catch (cause) {
        if (requestGeneration !== generation || nextController.signal.aborted || canceled(cause))
          return
        const apiError = errorFrom(cause)
        if (apiError.kind === 'notFound') {
          notFound.value = true
          response.value = EMPTY_RESPONSE
          state.value = 'empty'
          return
        }
        error.value = apiError
        state.value = 'error'
      } finally {
        if (requestGeneration === generation) pending = null
      }
    })()
    pending = request
    return request
  }

  function cancel(): void {
    controller?.abort()
    controller = null
    pending = null
  }

  function resetState(): void {
    cancel()
    generation += 1
    activeKey = null
    response.value = EMPTY_RESPONSE
    state.value = 'empty'
    notFound.value = false
    error.value = null
  }

  return {
    response,
    state,
    notFound,
    error,
    load,
    cancel,
    resetState,
  }
})
